import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CatalogGrid from './CatalogGrid';
import CategoryFilter from './CategoryFilter';

const CatalogGridView = ({ 
  products, 
  categories, 
  collections, 
  loading, 
  error,
  onCategoryChange,
  onCollectionChange,
  selectedCategory,
  selectedCollection
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('sku') || '');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filter and sort products
  let filteredProducts = [...products];
  
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    filteredProducts = filteredProducts.filter(product =>
      product.name.toLowerCase().includes(searchLower) ||
      product.sku.toLowerCase().includes(searchLower)
    );
  }
  
  // Client-side sorting
  switch (sortBy) {
    case 'sku':
      filteredProducts.sort((a, b) => a.sku.localeCompare(b.sku));
      break;
    case 'category':
      filteredProducts.sort((a, b) => a.category.localeCompare(b.category));
      break;
    case 'name':
    default:
      filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
  }

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value) {
      searchParams.set('sku', value);
    } else {
      searchParams.delete('sku');
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-light text-bmb-text mb-2">{t('catalog.title')}</h1>
          <p className="text-gray-600">{t('catalog.subtitle')}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="lg:hidden flex items-center justify-center py-2 px-4 bg-white rounded-lg shadow-sm"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {t('catalog.filters.title')}
          </button>

          {/* Sidebar Filters */}
          <aside className={`lg:block lg:w-64 ${isFilterOpen ? 'block' : 'hidden'}`}>
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={onCategoryChange}
              collections={collections}
              selectedCollection={selectedCollection}
              onCollectionChange={onCollectionChange}
            />
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search and Sort */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  placeholder={t('catalog.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bmb-text"
                />
                
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bmb-text cursor-pointer"
                  >
                    <option value="name">{t('catalog.sort.by_name')}</option>
                    <option value="sku">{t('catalog.sort.by_sku')}</option>
                    <option value="category">{t('catalog.sort.by_category')}</option>
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <CatalogGrid 
              products={filteredProducts} 
              loading={loading} 
              error={error} 
              searchTerm={searchTerm} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogGridView;